import { useTheme } from '@karnov/kui-react'
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { queryKeys } from 'api/consts'
import { useCreateTrackingEvent } from 'api/events/mutations'
import { FetchUserResponse } from 'api/user/types'
import { DocumentAnalysisMode } from 'consts/documentAnalysisModes'
import { useAppSourceTracker } from 'hooks/useAppSourceTracker'
import { useAppSource } from 'stores/useAppSourceStore'

import { queryClient } from './reactQuery'

import type { FC, ReactNode } from 'react'

import type { TriggerEvent } from 'api/events/types'

export type PageContext = {
  contextType?: 'conversations_page' | 'front_page' | 'document_analysis_page' | 'document_analysis_overview_page'
  contextDetails?: {
    conversation_id?: string
    document_analysis_mode?: DocumentAnalysisMode
  }
}

type Props = { children: ReactNode }

export const TrackingEventsContext = createContext<{
  triggerEvent: TriggerEvent
  updatePageContext: (pageContext: PageContext) => void
}>({
  triggerEvent: () => undefined,
  updatePageContext: () => undefined,
})

export const TrackingEventsProvider: FC<Props> = ({ children }) => {
  const [pageContext, setPageContext] = useState<PageContext>()
  const pageContextRef = useRef<PageContext>()

  useEffect(() => {
    pageContextRef.current = pageContext
  }, [pageContext])

  useAppSourceTracker()

  const appSource = useAppSource()

  const { isOSDarkMode, currentTheme } = useTheme()

  const { mutate: createTrackingEvent } = useCreateTrackingEvent()

  const userData = queryClient.getQueryData<FetchUserResponse>([queryKeys.user])
  const userGroupFeatures = userData?.user?.userGroupFeatures

  const updatePageContext = useCallback((updatedPageContext: PageContext) => {
    setPageContext(updatedPageContext)
  }, [])

  const triggerEvent = useCallback<TriggerEvent>(
    ({ name, properties }) => {
      createTrackingEvent({
        name,
        properties: {
          appSource,
          pageContext: pageContextRef.current,
          userSettings: {
            theme: currentTheme,
            systemTheme: isOSDarkMode ? 'dark' : 'light',
          },
          userGroupFeatures,
          ...properties,
        },
      })
    },
    [appSource, createTrackingEvent, currentTheme, isOSDarkMode, userGroupFeatures],
  )

  const value = useMemo(
    () => ({
      triggerEvent,
      updatePageContext,
    }),
    [triggerEvent, updatePageContext],
  )

  return (
    <TrackingEventsContext.Provider value={value}>
      {children}
    </TrackingEventsContext.Provider>
  )
}
